const body = document.body;
const siteHeader = document.querySelector(".site-header");
const menuToggle = document.querySelector(".menu-toggle");
const mainNav = document.querySelector(".main-nav");

const setMenuOpen = (isOpen) => {
  body.classList.toggle("menu-open", isOpen);
  siteHeader?.classList.toggle("nav-open", isOpen);
  menuToggle?.setAttribute("aria-expanded", String(isOpen));
  if (mainNav) mainNav.hidden = !isOpen;
};

if (menuToggle && mainNav) {
  mainNav.hidden = true;
  menuToggle.addEventListener("click", (event) => {
    event.preventDefault();
    setMenuOpen(menuToggle.getAttribute("aria-expanded") !== "true");
  });

  mainNav.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => setMenuOpen(false));
  });
}

const showToast = (message) => {
  const existing = document.querySelector(".toast");
  if (existing) existing.remove();
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  document.body.appendChild(toast);
  window.setTimeout(() => toast.remove(), 2400);
};

const countEls = document.querySelectorAll(".cart-count");
let cartCount = 0;

document.addEventListener("click", (event) => {
  if (!event.target.closest?.(".add-cart")) return;
  cartCount += 1;
  countEls.forEach((el) => {
    el.textContent = String(cartCount);
  });
  showToast("Added to cart for the prototype.");
});

const chips = document.querySelectorAll(".filter-chip");
const typeFilter = document.querySelector("[data-type-filter]");
const formatFilter = document.querySelector("[data-format-filter]");
const conditionFilter = document.querySelector("[data-condition-filter]");
const titleSearch = document.querySelector("[data-title-search]");
const filterStatus = document.querySelector("[data-filter-status]");
const catalogGrid = document.querySelector("[data-catalog-grid]");
const catalogPagination = document.querySelector("[data-catalog-pagination]");
let activeCategory = "all";
let catalogLoadPromise = null;
let catalogPagesLoaded = Number(catalogGrid?.dataset.totalPages || 1) <= 1;
let filterRun = 0;

const categoryAliases = {
  books: ["books", "book"],
  bundles: ["bundles", "bundle"],
  holiday: ["holiday", "christmas"],
  "monthly-club": ["monthly-club", "monthly"],
  goods: ["goods", "bookish-goods"]
};

const normalizeToken = (value = "") => value.toLowerCase().trim().replace(/[_\s]+/g, "-");

const getCatalogProducts = () => catalogGrid
  ? catalogGrid.querySelectorAll(".product-card[data-categories]")
  : [];

const productCardKey = (card) => card.querySelector("a[href*='/products/']")?.getAttribute("href") || "";

const loadRemainingCatalogPages = async ({ announce = true } = {}) => {
  if (!catalogGrid || catalogPagesLoaded) return;
  if (catalogLoadPromise) return catalogLoadPromise;

  catalogLoadPromise = (async () => {
    const totalPages = Number(catalogGrid.dataset.totalPages || 1);
    const currentPage = Number(catalogGrid.dataset.currentPage || 1);
    const collectionUrl = catalogGrid.dataset.collectionUrl || window.location.pathname;
    const sectionId = catalogGrid.dataset.sectionId;
    const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1)
      .filter((pageNumber) => pageNumber !== currentPage);

    if (announce && filterStatus) filterStatus.textContent = "Loading all matching products...";

    const pageCards = await Promise.all(pageNumbers.map(async (pageNumber) => {
      const pageUrl = new URL(collectionUrl, window.location.origin);
      pageUrl.searchParams.set("page", String(pageNumber));
      if (sectionId) pageUrl.searchParams.set("section_id", sectionId);
      const response = await fetch(pageUrl.toString(), {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });
      if (!response.ok) throw new Error(`Catalog page ${pageNumber} failed to load`);
      const pageDocument = new DOMParser().parseFromString(await response.text(), "text/html");
      return [...pageDocument.querySelectorAll("[data-catalog-grid] .product-card[data-categories]")];
    }));

    const knownProducts = new Set([...getCatalogProducts()].map(productCardKey).filter(Boolean));
    const fetchedCards = document.createDocumentFragment();
    pageCards.flat().forEach((card) => {
      const key = productCardKey(card);
      if (key && knownProducts.has(key)) return;
      if (key) knownProducts.add(key);
      card.dataset.catalogFetched = "true";
      card.hidden = true;
      card.classList.add("is-filtered-out");
      card.setAttribute("aria-hidden", "true");
      fetchedCards.appendChild(card);
    });
    catalogGrid.appendChild(fetchedCards);

    catalogPagesLoaded = true;
  })();

  try {
    await catalogLoadPromise;
  } finally {
    catalogLoadPromise = null;
  }
};

// Dropdown values are maintained in the theme, while the catalog changes in
// Shopify. Remove a value when there is no product in the complete catalog
// that can satisfy it, so shoppers are never sent to an empty result set.
const removeUnavailableFilterOptions = () => {
  [
    [typeFilter, "type"],
    [formatFilter, "format"],
    [conditionFilter, "condition"]
  ].forEach(([filter, dataKey]) => {
    if (!filter) return;
    const availableValues = new Set([...getCatalogProducts()]
      .map((card) => normalizeToken(card.dataset[dataKey]))
      .filter(Boolean));

    [...filter.options].forEach((option) => {
      const value = normalizeToken(option.value);
      if (value !== "all" && !availableValues.has(value)) option.remove();
    });
  });
};

const prepareCatalogFilterOptions = async () => {
  if (!catalogGrid) return;
  try {
    await loadRemainingCatalogPages({ announce: false });
    removeUnavailableFilterOptions();
  } catch (error) {
    // Keep the existing options if the full catalog cannot be verified.
    console.error(error);
  }
};

const applyCatalogFilters = async () => {
  const thisRun = ++filterRun;
  const activeType = normalizeToken(typeFilter ? typeFilter.value : "all");
  const activeFormat = normalizeToken(formatFilter ? formatFilter.value : "all");
  const activeCondition = normalizeToken(conditionFilter ? conditionFilter.value : "all");
  const activeTitleQuery = titleSearch ? titleSearch.value.trim().toLocaleLowerCase() : "";
  const isFiltered = activeCategory !== "all" || activeType !== "all" || activeFormat !== "all" || activeCondition !== "all" || activeTitleQuery !== "";
  let completeCatalogAvailable = catalogPagesLoaded;

  if (catalogPagination && isFiltered) catalogPagination.hidden = true;
  if (catalogGrid) catalogGrid.setAttribute("aria-busy", String(isFiltered && !catalogPagesLoaded));

  if (isFiltered && !catalogPagesLoaded) {
    try {
      await loadRemainingCatalogPages();
      completeCatalogAvailable = true;
    } catch (error) {
      completeCatalogAvailable = false;
      console.error(error);
    }
  }

  if (thisRun !== filterRun) return;

  if (catalogGrid) catalogGrid.setAttribute("aria-busy", "false");

  let visibleCount = 0;
  getCatalogProducts().forEach((card) => {
    const categories = (card.dataset.categories || "").split(/\s+/).map(normalizeToken).filter(Boolean);
    const aliases = categoryAliases[activeCategory] || [activeCategory];
    const matchesCategory = activeCategory === "all" || aliases.some((category) => categories.includes(category));
    const matchesType = activeType === "all" || normalizeToken(card.dataset.type) === activeType;
    const matchesFormat = activeFormat === "all" || normalizeToken(card.dataset.format) === activeFormat;
    const matchesCondition = activeCondition === "all" || normalizeToken(card.dataset.condition) === activeCondition;
    const productTitle = (card.dataset.title || card.querySelector("h2")?.textContent || "").toLocaleLowerCase();
    const matchesTitle = activeTitleQuery === "" || productTitle.includes(activeTitleQuery);
    const belongsToAnotherPage = card.dataset.catalogFetched === "true";
    const isVisible = isFiltered ? matchesCategory && matchesType && matchesFormat && matchesCondition && matchesTitle : !belongsToAnotherPage;
    const shouldHide = !isVisible;
    if (card.hidden !== shouldHide) card.hidden = shouldHide;
    if (card.classList.contains("is-filtered-out") !== shouldHide) {
      card.classList.toggle("is-filtered-out", shouldHide);
    }
    if (card.getAttribute("aria-hidden") !== String(shouldHide)) {
      card.setAttribute("aria-hidden", String(shouldHide));
    }
    if (isVisible) visibleCount += 1;
  });

  if (catalogPagination) catalogPagination.hidden = isFiltered && completeCatalogAvailable;
  if (filterStatus) {
    const suffix = !isFiltered && Number(catalogGrid?.dataset.totalPages || 1) > 1 ? " on this page" : " shown";
    filterStatus.textContent = completeCatalogAvailable || !isFiltered
      ? `${visibleCount} ${visibleCount === 1 ? "item" : "items"}${suffix}`
      : `${visibleCount} ${visibleCount === 1 ? "item" : "items"} shown; additional pages could not be loaded`;
  }
};

const selectCategory = (category, { resetType = false, updateUrl = false } = {}) => {
  const normalizedCategory = normalizeToken(category);
  const requestedChip = [...chips].find((chip) => normalizeToken(chip.dataset.filter) === normalizedCategory);
  activeCategory = requestedChip ? normalizedCategory : "all";
  if (resetType && typeFilter) typeFilter.value = "all";
  chips.forEach((item) => item.classList.toggle("active", normalizeToken(item.dataset.filter) === activeCategory));
  if (updateUrl && window.history?.replaceState) {
    const nextHash = activeCategory === "all" ? window.location.pathname + window.location.search : `#${activeCategory}`;
    window.history.replaceState(null, "", nextHash);
  }
  void applyCatalogFilters();
};

chips.forEach((chip) => {
  chip.addEventListener("click", () => {
    selectCategory(chip.dataset.filter, { resetType: true, updateUrl: true });
  });
});

if (typeFilter) {
  typeFilter.addEventListener("change", () => void applyCatalogFilters());
}

if (formatFilter) {
  formatFilter.addEventListener("change", () => void applyCatalogFilters());
}

if (conditionFilter) {
  conditionFilter.addEventListener("change", () => void applyCatalogFilters());
}

if (titleSearch) {
  titleSearch.addEventListener("input", () => void applyCatalogFilters());
}

if (getCatalogProducts().length) {
  const requestedCategory = decodeURIComponent(window.location.hash.slice(1)) || "all";
  // The server-rendered catalog already represents the unfiltered state. Avoid
  // rewriting every card at startup; this also leaves the reserved status line
  // untouched and prevents third-party observers from measuring between writes.
  if (normalizeToken(requestedCategory) !== "all") selectCategory(requestedCategory);
  window.addEventListener("hashchange", () => {
    selectCategory(decodeURIComponent(window.location.hash.slice(1)) || "all", { resetType: true });
  });
  void prepareCatalogFilterOptions();
}

const modal = document.querySelector("[data-modal]");
const closeButton = document.querySelector("[data-close]");

const closeModal = () => {
  if (!modal) return;
  modal.classList.remove("open");
  modal.setAttribute("aria-hidden", "true");
};

if (modal && document.body.dataset.page === "home") {
  window.setTimeout(() => {
    modal.classList.add("open");
    modal.setAttribute("aria-hidden", "false");
  }, 900);
}

if (closeButton) {
  closeButton.addEventListener("click", closeModal);
}

if (modal) {
  modal.addEventListener("click", (event) => {
    if (event.target === modal) closeModal();
  });
}

/*
 * Shopify Subscriptions normally copies the selected plan into its hidden
 * selling_plan field. Keep that field synchronized as a defensive fallback so
 * subscription-only variants cannot be submitted without their selling plan.
 */
const initializeSubscriptionSellingPlanFallback = () => {
  const widget = document.querySelector(".subscriptions_app_embed_block");
  if (!widget) return;

  const productForm = widget.closest("form[action*='/cart/add']");
  if (!productForm) return;

  const syncSellingPlan = () => {
    const variantId = productForm.querySelector("[name='id']")?.value;
    const variantSection = variantId
      ? widget.querySelector(`[data-variant-id="${variantId}"]`)
      : null;
    const planScope = variantSection || widget;
    const oneTimePurchase = planScope.querySelector("input[data-radio-type='one_time_purchase']:checked");
    const selectedPlan = planScope.querySelector("input[data-selling-plan-id]:checked");
    let planInput = productForm.querySelector("input[name='selling_plan']");

    if (!planInput && selectedPlan) {
      planInput = document.createElement("input");
      planInput.type = "hidden";
      planInput.name = "selling_plan";
      planInput.dataset.themeSellingPlanFallback = "true";
      productForm.appendChild(planInput);
    }

    if (!planInput) return;

    if (oneTimePurchase) {
      planInput.value = "";
    } else if (selectedPlan?.dataset.sellingPlanId) {
      planInput.value = selectedPlan.dataset.sellingPlanId;
    }
  };

  syncSellingPlan();

  productForm.addEventListener("change", (event) => {
    if (event.target.matches("[name='id'], input[data-radio-type], input[data-selling-plan-id]")) {
      window.setTimeout(syncSellingPlan, 0);
    }
  });

  productForm.addEventListener("submit", syncSellingPlan, true);

  if ("MutationObserver" in window) {
    const observer = new MutationObserver(syncSellingPlan);
    observer.observe(widget, { childList: true, subtree: true });
  }
};

initializeSubscriptionSellingPlanFallback();

/*
 * The product page starts with the key Shopify image only. Other image URLs are
 * intentionally stored in a template and become real image requests only after
 * the shopper chooses an arrow. Collection and search cards never use this UI.
 */
document.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
  const image = gallery.querySelector(".product-gallery-image");
  const nextButton = gallery.querySelector("[data-gallery-next]");
  const previousButton = gallery.querySelector("[data-gallery-previous]");
  const status = gallery.querySelector("[data-gallery-status]");
  const imageTemplate = gallery.querySelector("template[data-gallery-images]");
  if (!image || !nextButton || !previousButton || !status || !imageTemplate) return;

  const secondaryImages = [...imageTemplate.content.querySelectorAll("[data-src]")].map((item) => ({
    src: item.dataset.src,
    srcset: item.dataset.srcset,
    alt: item.dataset.alt || image.alt
  }));
  const images = [{ src: image.currentSrc || image.src, srcset: image.srcset, alt: image.alt }, ...secondaryImages];
  let activeIndex = 0;

  const updateControls = () => {
    previousButton.disabled = activeIndex === 0;
    nextButton.disabled = activeIndex === images.length - 1;
    status.textContent = `Image ${activeIndex + 1} of ${images.length}`;
  };

  const showImage = (nextIndex) => {
    const nextImage = images[nextIndex];
    if (!nextImage || nextIndex === activeIndex) return;
    activeIndex = nextIndex;
    // Assigning source attributes here, rather than emitting secondary <img>s in
    // Liquid, defers every non-primary image request until this interaction.
    image.srcset = nextImage.srcset;
    image.src = nextImage.src;
    image.alt = nextImage.alt;
    image.removeAttribute("fetchpriority");
    image.loading = "eager";
    updateControls();
  };

  previousButton.addEventListener("click", () => showImage(activeIndex - 1));
  nextButton.addEventListener("click", () => showImage(activeIndex + 1));
  updateControls();
});
